import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUrl'; 
import config from '../config';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Car, MapPin, Fuel, Settings, Calendar, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import API from '../services/api';
import { contactWhatsappLocation } from '../services/api';



const Calendrier = ({ reservations, dateDebut, dateFin, onSelectDate }) => {
  const [moisActif, setMoisActif] = useState(new Date());
  const moisNoms = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  const today = new Date();
  const todayStr = today.getFullYear()+'-'+String(today.getMonth()+1).padStart(2,'0')+'-'+String(today.getDate()).padStart(2,'0');

  const premierJour = new Date(moisActif.getFullYear(), moisActif.getMonth(), 1);
  const dernierJour = new Date(moisActif.getFullYear(), moisActif.getMonth()+1, 0);
  const joursVides = premierJour.getDay()===0 ? 6 : premierJour.getDay()-1;
  const jours = [];
  for(let i=0;i<joursVides;i++) jours.push(null);
  for(let i=1;i<=dernierJour.getDate();i++) jours.push(i);

  const dsFromJour = (jour) => {
    const y = moisActif.getFullYear();
    const m = String(moisActif.getMonth()+1).padStart(2,'0');
    const d = String(jour).padStart(2,'0');
    return y+'-'+m+'-'+d;
  };

  const isReserved = (ds) => reservations.some(r => {
    const deb = r.date_debut.substring(0,10);
    const fin = r.date_fin.substring(0,10);
    return ds>=deb && ds<=fin;
  });

  const isPast = (ds) => ds < todayStr;
  const isSelectable = (ds) => !isPast(ds) && !isReserved(ds);

  const getStyle = (jour) => {
    if(!jour) return {bg:'transparent',color:'transparent',clickable:false};
    const ds = dsFromJour(jour);
    const past = isPast(ds);
    const reserved = isReserved(ds);
    const selected = ds === dateDebut || ds === dateFin;
    const inRange = dateDebut && dateFin && ds > dateDebut && ds < dateFin;

    if(selected) return {bg:'#F5A623',color:'#000',clickable:true,weight:900};
    if(inRange) return {bg:'#fef3c7',color:'#92400e',clickable:true};
    if(reserved) return {bg:'#fecaca',color:'#dc2626',clickable:false};
    if(past) return {bg:'#f3f4f6',color:'#d1d5db',clickable:false};
    if(ds===todayStr) return {bg:'#dcfce7',color:'#16a34a',clickable:true,ring:'#F5A623'};
    return {bg:'#dcfce7',color:'#16a34a',clickable:true};
  };

  const handleClick = (jour) => {
    if(!jour || !onSelectDate) return;
    const ds = dsFromJour(jour);
    if(!isSelectable(ds)) return;
    onSelectDate(ds);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-3 border border-gray-100 mb-4">
      <h2 className="font-black text-sm mb-2 text-gray-900 flex items-center gap-1">
        <Calendar size={14} style={{color:'#F5A623'}}/> Choisir les dates
      </h2>
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={()=>setMoisActif(new Date(moisActif.getFullYear(),moisActif.getMonth()-1))} className="p-1 hover:bg-gray-100 rounded">
          <ChevronLeft size={12}/>
        </button>
        <p className="font-bold text-gray-900 text-xs">{moisNoms[moisActif.getMonth()]} {moisActif.getFullYear()}</p>
        <button type="button" onClick={()=>setMoisActif(new Date(moisActif.getFullYear(),moisActif.getMonth()+1))} className="p-1 hover:bg-gray-100 rounded">
          <ChevronRight size={12}/>
        </button>
      </div>
      <div className="grid grid-cols-7">
        {['L','M','M','J','V','S','D'].map((j,i)=>(
          <div key={i} className="text-center font-bold text-gray-400 py-0.5" style={{fontSize:'9px'}}>{j}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5 mt-0.5">
        {jours.map((jour,i)=>{
          const s = getStyle(jour);
          return (
            <div
              key={i}
              onClick={()=>handleClick(jour)}
              style={{
                background:s.bg,
                color:s.color,
                fontSize:'10px',
                height:'26px',
                fontWeight:s.weight||600,
                cursor:s.clickable?'pointer':'default',
                boxShadow:s.ring?`inset 0 0 0 1px ${s.ring}`:'none',
                opacity:s.clickable||!jour?1:0.85
              }}
              className="flex items-center justify-center rounded transition hover:opacity-80"
            >
              {jour}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded" style={{background:'#dcfce7'}}/><span className="text-gray-400" style={{fontSize:'9px'}}>Dispo</span></div>
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded" style={{background:'#fecaca'}}/><span className="text-gray-400" style={{fontSize:'9px'}}>Indispo</span></div>
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded" style={{background:'#F5A623'}}/><span className="text-gray-400" style={{fontSize:'9px'}}>Sélection</span></div>
        <div className="flex items-center gap-1"><div className="w-2 h-2 rounded" style={{background:'#fef3c7'}}/><span className="text-gray-400" style={{fontSize:'9px'}}>Plage</span></div>
      </div>
    </div>
  );
};

const DetailLocation = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoActive, setPhotoActive] = useState(0);
  const [form, setForm] = useState({ date_debut: '', date_fin: '', message: '' });
  const [calcul, setCalcul] = useState(null);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');

  useEffect(() => {
    API.get('/locations/'+id)
      .then(res => { setLocation(res.data.location); setReservations(res.data.reservations); })
      .catch(() => navigate('/locations'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if(!form.date_debut || !form.date_fin || !location) { setCalcul(null); return; }
    const nb_jours = Math.ceil((new Date(form.date_fin)-new Date(form.date_debut))/(1000*60*60*24)) + 1;
    if(nb_jours<=0) { setCalcul(null); return; }
    setCalcul({ nb_jours, prix_total: location.prix_jour * nb_jours });
  }, [form.date_debut, form.date_fin, location]);

  const rangeOverlapsReservation = (debut, fin) => {
    return reservations.some(r => {
      const rDeb = r.date_debut.substring(0,10);
      const rFin = r.date_fin.substring(0,10);
      return debut <= rFin && fin >= rDeb;
    });
  };

  const handleSelectDate = (ds) => {
    setErreur('');
    if(!form.date_debut || (form.date_debut && form.date_fin)) {
      setForm({ ...form, date_debut: ds, date_fin: '' });
      return;
    }
    if(ds < form.date_debut) {
      setForm({ ...form, date_debut: ds, date_fin: '' });
      return;
    }
    if(ds === form.date_debut) {
      setForm({ ...form, date_fin: ds });
      return;
    }
    if(rangeOverlapsReservation(form.date_debut, ds)) {
      setErreur('La plage sélectionnée chevauche une réservation existante.');
      setForm({ ...form, date_debut: ds, date_fin: '' });
      return;
    }
    setForm({ ...form, date_fin: ds });
  };

  const formaterDate = (ds) => {
    if(!ds) return '';
    const [y,m,d] = ds.split('-');
    return `${d}/${m}/${y}`;
  };

  const handleReserver = async (e) => {
    e.preventDefault();
    if(!user) return navigate('/connexion');
    if(!form.date_debut || !form.date_fin) { setErreur('Veuillez sélectionner les dates dans le calendrier.'); return; }
    setErreur('');
    try {
      await API.post('/locations/reservations/creer', { location_id: id, ...form });
      setSucces('✅ Demande envoyée ! Le vendeur vous contactera.');
      setForm({ date_debut: '', date_fin: '', message: '' });
      setCalcul(null);
    } catch(err) {
      setErreur(err.response?.data?.message || 'Erreur lors de la réservation');
    }
  };

  if(loading) return <div className="min-h-screen bg-gray-50"><Navbar /><div className="text-center py-20 text-gray-400">Chargement...</div></div>;
  if(!location) return null;
  const photos = location.photos ? location.photos.filter(p=>p!==null) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div style={{background:'#0A0A0A'}} className="px-4 py-3">
        <div className="max-w-7xl mx-auto text-sm text-gray-400">
          <Link to="/" className="hover:text-yellow-400">Accueil</Link>
          <span className="mx-2">›</span>
          <Link to="/locations" className="hover:text-yellow-400">Locations</Link>
          <span className="mx-2">›</span>
          <span className="text-white">{location.titre}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

          {/* Gauche */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm mb-6 border border-gray-100">
              <div className="overflow-hidden relative" style={{maxHeight:'450px'}}>
                {photos.length>0 ? (
                  <img src={getImageUrl(photos[photoActive])} alt={location.titre} className="w-full object-cover" style={{maxHeight:'450px'}}/>
                ) : (
                  <div className="h-80 flex items-center justify-center bg-gray-100"><Car size={80} className="text-gray-300"/></div>
                )}
                <div style={{background:'#F5A623'}} className="absolute top-3 left-3 text-black text-xs font-black px-3 py-1 rounded-full">📅 Location</div>
              </div>
              {photos.length>1 && (
                <div className="flex gap-2 p-3 overflow-x-auto">
                  {photos.map((photo,index)=>(
                    <div key={index} onClick={()=>setPhotoActive(index)}
                      style={{borderColor:photoActive===index?'#F5A623':'transparent'}}
                      className="h-16 w-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden cursor-pointer border-2">
                      <img src={getImageUrl(photo)} alt="" className="w-full h-full object-cover"/>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 border border-gray-100">
              <h1 className="text-2xl font-black text-gray-900 mb-4">{location.titre}</h1>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div style={{background:'#F5A62310',border:'1px solid #F5A62330'}} className="rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">Par jour</p>
                  <p className="font-black text-gray-900">{parseInt(location.prix_jour).toLocaleString('fr-FR')} FCFA</p>
                </div>
                {location.prix_semaine && <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-500 mb-1">Par semaine</p><p className="font-black text-gray-900">{parseInt(location.prix_semaine).toLocaleString('fr-FR')} FCFA</p></div>}
                {location.prix_mois && <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-500 mb-1">Par mois</p><p className="font-black text-gray-900">{parseInt(location.prix_mois).toLocaleString('fr-FR')} FCFA</p></div>}
              </div>
              {location.caution && <p className="text-sm text-gray-500">🔐 Caution : <strong>{parseInt(location.caution).toLocaleString('fr-FR')} FCFA</strong></p>}
              {location.localisation && <div className="flex items-center gap-2 text-gray-500 mt-3 text-sm"><MapPin size={16} style={{color:'#F5A623'}}/><span>{location.localisation}</span></div>}
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 border border-gray-100">
              <h2 className="font-black text-lg mb-4 text-gray-900 flex items-center gap-2"><span style={{color:'#F5A623'}}>|</span> Caractéristiques</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[{icon:Calendar,label:'Année',value:location.annee},{icon:Fuel,label:'Carburant',value:location.carburant},{icon:Settings,label:'Transmission',value:location.transmission},{icon:MapPin,label:'Localisation',value:location.localisation}]
                  .map(({icon:Icon,label,value})=>value&&(
                    <div key={label} className="text-center p-4 bg-gray-50 rounded-xl">
                      <Icon size={24} className="mx-auto mb-2" style={{color:'#F5A623'}}/>
                      <p className="text-xs text-gray-500 mb-1">{label}</p>
                      <p className="font-bold text-gray-900 capitalize text-sm">{value}</p>
                    </div>
                  ))}
              </div>
            </div>

            {location.description && (
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                <h2 className="font-black text-lg mb-4 text-gray-900 flex items-center gap-2"><span style={{color:'#F5A623'}}>|</span> Description</h2>
                <p className="text-gray-600 leading-relaxed">{location.description}</p>
              </div>
            )}
          </div>

          {/* Droite */}
          <div>
            {/* Proprietaire */}
            <div className="bg-white rounded-2xl shadow-sm p-5 mb-4 border border-gray-100">
              <h2 className="font-black text-sm mb-3 text-gray-900 flex items-center gap-2"><span style={{color:'#F5A623'}}>|</span> Propriétaire</h2>
              <div className="flex items-center gap-3 mb-3">
                <div style={{background:'#F5A623'}} className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-black font-black">{location.prenom?.[0]}{location.nom?.[0]}</span>
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{location.prenom} {location.nom}</p>
                  {location.telephone && <p className="text-xs text-gray-500 flex items-center gap-1"><Phone size={10}/> {location.telephone}</p>}
                </div>
              </div>

              {/* Bouton WhatsApp */}
              {location.telephone && (
                <a
                  href={`https://wa.me/${location.telephone.replace(/\s/g, '')}?text=${encodeURIComponent(`Bonjour, je suis intéressé par votre location "${location.titre}" à ${parseInt(location.prix_jour).toLocaleString('fr-FR')} FCFA/jour`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { contactWhatsappLocation(location.id).catch(() => {}); }}
                  style={{ background: '#25D366' }}
                  className="w-full flex items-center justify-center gap-2 text-white rounded-xl py-2 text-xs font-bold hover:opacity-90 transition"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  Contacter sur WhatsApp
                </a>
              )}
            </div>

            {/* Calendrier compact */}
            <Calendrier
              reservations={reservations}
              dateDebut={form.date_debut}
              dateFin={form.date_fin}
              onSelectDate={handleSelectDate}
            />

            {/* Formulaire reservation */}
            <div className="bg-white rounded-2xl shadow-sm p-5 mb-4 border border-gray-100">
              <h2 className="font-black text-sm mb-3 text-gray-900 flex items-center gap-2">
                <Calendar size={16} style={{color:'#F5A623'}}/> Réserver ce véhicule
              </h2>
              {succes ? (
                <div className="bg-green-50 border border-green-200 text-green-600 p-3 rounded-xl text-xs font-semibold">{succes}</div>
              ) : (
                <form onSubmit={handleReserver} className="space-y-3">
                  {erreur && <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-xl text-xs">{erreur}</div>}
                  <div className="grid grid-cols-2 gap-2">
                    <div style={{background:'#F5A62310',border:'1px solid #F5A62330'}} className="rounded-xl p-2.5">
                      <p className="text-[10px] font-semibold text-gray-500 mb-0.5">Début</p>
                      <p className="text-sm font-black text-gray-900">{form.date_debut ? formaterDate(form.date_debut) : '—'}</p>
                    </div>
                    <div style={{background:'#F5A62310',border:'1px solid #F5A62330'}} className="rounded-xl p-2.5">
                      <p className="text-[10px] font-semibold text-gray-500 mb-0.5">Fin</p>
                      <p className="text-sm font-black text-gray-900">{form.date_fin ? formaterDate(form.date_fin) : '—'}</p>
                    </div>
                  </div>
                  {!form.date_debut && (
                    <p className="text-[11px] text-gray-500 italic">Sélectionnez vos dates dans le calendrier ci-dessus.</p>
                  )}
                  {form.date_debut && !form.date_fin && (
                    <p className="text-[11px] text-gray-500 italic">Cliquez sur la date de fin dans le calendrier.</p>
                  )}
                  {(form.date_debut || form.date_fin) && (
                    <button type="button" onClick={()=>{setForm({...form,date_debut:'',date_fin:''});setCalcul(null);setErreur('');}}
                      className="text-xs text-gray-500 hover:text-gray-700 underline">
                      Réinitialiser les dates
                    </button>
                  )}
                  {calcul && (
                    <div style={{background:'#F5A62310',border:'1px solid #F5A62330'}} className="rounded-xl p-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{calcul.nb_jours} jour(s)</span>
                        <span className="font-black text-gray-900">{parseInt(calcul.prix_total).toLocaleString('fr-FR')} FCFA</span>
                      </div>
                      {location.caution && <div className="flex justify-between text-xs mt-1"><span className="text-gray-500">Caution</span><span className="text-gray-600">{parseInt(location.caution).toLocaleString('fr-FR')} FCFA</span></div>}
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Message (optionnel)</label>
                    <textarea value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} rows={2}
                      placeholder="Précisez votre besoin..."
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 resize-none"/>
                  </div>
                  {user ? (
                    <button type="submit" disabled={!form.date_debut || !form.date_fin}
                      style={{background:'#F5A623'}}
                      className="w-full text-black py-2.5 rounded-xl font-black hover:opacity-90 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                      Envoyer la demande
                    </button>
                  ) : (
                    <button type="button" onClick={()=>navigate('/connexion')} style={{background:'#F5A623'}} className="w-full text-black py-2.5 rounded-xl font-black hover:opacity-90 transition text-sm">
                      Se connecter pour réserver
                    </button>
                  )}
                </form>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailLocation;